import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { 
  User, 
  Users, 
  MapPin, 
  PhoneCall, 
  Stethoscope, 
  Activity, 
  ClipboardCheck, 
  ShieldCheck, 
  HeartPulse,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

const STEPS = [
  { id: 'child', title: 'Child Information', icon: User },
  { id: 'parent', title: 'Parent / Guardian', icon: Users },
  { id: 'address', title: 'Address & Contact', icon: MapPin },
  { id: 'consent', title: 'Consent & Submit', icon: ShieldCheck },
];

const QuestionRow = ({ label, name, options, value, onChange, error }) => (
  <div className="py-4 border-b border-slate-50 last:border-0">
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <p className="text-slate-700 font-medium leading-relaxed max-w-xl">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange({ target: { name, value: opt } })}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border-2 
              ${value === opt 
                ? 'bg-brand-600 border-brand-600 text-white shadow-md' 
                : 'bg-white border-slate-100 text-slate-400 hover:border-brand-200 hover:text-brand-600'}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
    {error && <p className="mt-2 text-xs text-red-500 flex items-center gap-1 font-semibold"><AlertCircle size={12}/> {error}</p>}
  </div>
);

export default function PatientRegistrationForm({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem('pr_draft_step');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [formData, setFormData] = useState(() => {
    const defaultData = {
      // Step 1: Child Info
      childFullName: '', ageYears: '', ageMonths: '', dob: '', gender: '', birthWeight: '', bloodGroup: '',
      // Step 2: Parent Info
      parentName: '', relationship: '', mobilePrimary: '', mobileAlternate: '', email: '', password: '', pcMainConcern: '',
      // Step 3: Address & Emergency
      address: '', city: '', state: '', pincode: '', emergencyName: '', emergencyRelationship: '', emergencyPhone: '',
      // Step 4: Consent & Submit
      insuranceCompany: '', policyNumber: '', consentGuardian: false, consentPolicies: false,
    };
    const saved = localStorage.getItem('pr_draft_data');
    return saved ? { ...defaultData, ...JSON.parse(saved) } : defaultData;
  });

  useEffect(() => {
    localStorage.setItem('pr_draft_step', currentStep.toString());
    localStorage.setItem('pr_draft_data', JSON.stringify(formData));
  }, [currentStep, formData]);

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let updates = {
      [name]: type === 'checkbox' ? checked : value
    };

    if (name === 'dob' && value) {
        const dobDate = new Date(value);
        if (!isNaN(dobDate)) {
            const today = new Date();
            let years = today.getFullYear() - dobDate.getFullYear();
            let months = today.getMonth() - dobDate.getMonth();
            
            if (today.getDate() < dobDate.getDate()) {
                months--;
            }
            if (months < 0) {
                years--;
                months += 12;
            }

            // Ensure we don't set negative values if future date is accidentally picked
            updates.ageYears = Math.max(0, years).toString();
            updates.ageMonths = Math.max(0, months).toString();
            
            // Clear age errors automatically when calculated
            setErrors(prev => ({ ...prev, ageYears: '', dob: '' }));
        }
    }

    setFormData(prev => ({
      ...prev,
      ...updates
    }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};
    const step = STEPS[stepIndex].id;

    if (step === 'child') {
      if (!formData.childFullName) newErrors.childFullName = 'Full name is required';
      if (!formData.dob) newErrors.dob = 'Date of birth is required';
      if (!formData.gender) newErrors.gender = 'Gender is required';
      if (!formData.pcMainConcern) newErrors.pcMainConcern = 'Please specify the main problem or concern';
    } else if (step === 'parent') {
      if (!formData.parentName) newErrors.parentName = 'Parent name is required';
      if (!formData.relationship) newErrors.relationship = 'Relationship is required';
      if (!formData.mobilePrimary) newErrors.mobilePrimary = 'Mobile number is required';
      if (!formData.email) newErrors.email = 'Email address is required';
      if (!formData.password) newErrors.password = 'Password is required';
    } else if (step === 'address') {
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.pincode) newErrors.pincode = 'Pincode is required';
      if (!formData.emergencyName) newErrors.emergencyName = 'Contact name is required';
      if (!formData.emergencyPhone) newErrors.emergencyPhone = 'Phone number is required';
    } else if (step === 'consent') {
      if (!formData.consentGuardian) newErrors.consentGuardian = 'Confirmation required';
      if (!formData.consentPolicies) newErrors.consentPolicies = 'Agreement required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
      } else {
        handleSubmit();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = () => {
    localStorage.removeItem('pr_draft_step');
    localStorage.removeItem('pr_draft_data');
    const patientId = 'PAT-' + Math.random().toString(36).substr(2, 9).toUpperCase();
    onComplete({ ...formData, patientId });
  };

  const stepDetails = STEPS[currentStep];
  const Icon = stepDetails.icon;

  return (
    <div className="max-w-4xl mx-auto pb-20">
      {/* Progress Stepper */}
      <div className="mb-8 overflow-x-auto pb-4 custom-scrollbar">
        <div className="flex justify-between items-center min-w-[800px] px-2">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div key={step.id} className="flex flex-col items-center relative flex-1">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all border-2 z-10 
                    ${isActive ? 'bg-brand-600 border-brand-600 text-white ring-4 ring-brand-100' : 
                      isCompleted ? 'bg-brand-50 border-brand-500 text-brand-600' : 
                      'bg-white border-slate-200 text-slate-400'}`}
                >
                  {isCompleted ? <CheckCircle2 size={20} /> : <StepIcon size={20} />}
                </div>
                <span className={`mt-2 text-[10px] font-semibold uppercase tracking-wider text-center
                  ${isActive ? 'text-brand-700' : isCompleted ? 'text-brand-600' : 'text-slate-400'}`}>
                  {step.title}
                </span>
                {index < STEPS.length - 1 && (
                  <div className={`absolute top-5 left-[50%] w-full h-0.5 -z-0 
                    ${isCompleted ? 'bg-brand-500' : 'bg-slate-200'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Card className="p-8 border-none shadow-premium bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-brand-50 rounded-2xl text-brand-600">
            <Icon size={32} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{stepDetails.title}</h2>
            <p className="text-slate-500">Step {currentStep + 1} of {STEPS.length}</p>
          </div>
        </div>

        <div className="space-y-6">
          {currentStep === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Child Full Name *</label>
                <Input name="childFullName" value={formData.childFullName} onChange={handleChange} placeholder="Enter child's full name" className={errors.childFullName ? 'border-red-300' : ''} />
                {errors.childFullName && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12}/> {errors.childFullName}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Age (Years)</label>
                  <Input name="ageYears" type="number" min="0" max="18" value={formData.ageYears} onChange={handleChange} placeholder="Y" />
                  {errors.ageYears && <p className="mt-1 text-xs text-red-500">{errors.ageYears}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Age (Months)</label>
                  <Input name="ageMonths" type="number" min="0" max="11" value={formData.ageMonths} onChange={handleChange} placeholder="M" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Date of Birth *</label>
                <Input name="dob" type="date" value={formData.dob} onChange={handleChange} className={errors.dob ? 'border-red-300' : ''} />
                {errors.dob && <p className="mt-1 text-xs text-red-500">{errors.dob}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Gender *</label>
                <select name="gender" value={formData.gender} onChange={handleChange} className={`w-full h-10 px-3 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 ${errors.gender ? 'border-red-300' : 'border-slate-200'}`}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && <p className="mt-1 text-xs text-red-500">{errors.gender}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Birth Weight (optional)</label>
                <Input name="birthWeight" value={formData.birthWeight} onChange={handleChange} placeholder="e.g. 3.2 kg" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Blood Group (optional)</label>
                <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Primary Problem / Main Concern *</label>
                <textarea 
                  name="pcMainConcern" 
                  value={formData.pcMainConcern} 
                  onChange={handleChange} 
                  placeholder="Please describe the main problem, symptoms, or why you are seeking consultation..." 
                  className={`w-full p-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-brand-500 min-h-[100px] ${errors.pcMainConcern ? 'border-red-300' : 'border-slate-200'}`}
                />
                {errors.pcMainConcern && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12}/> {errors.pcMainConcern}</p>}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Parent/Guardian Name *</label>
                <Input name="parentName" value={formData.parentName} onChange={handleChange} placeholder="Full name of parent or guardian" />
                {errors.parentName && <p className="mt-1 text-xs text-red-500">{errors.parentName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Relationship to Child *</label>
                <select name="relationship" value={formData.relationship} onChange={handleChange} className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">Select Relationship</option>
                  <option value="Mother">Mother</option>
                  <option value="Father">Father</option>
                  <option value="Guardian">Guardian</option>
                </select>
                {errors.relationship && <p className="mt-1 text-xs text-red-500">{errors.relationship}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Mobile Number (Primary) *</label>
                <Input name="mobilePrimary" type="tel" value={formData.mobilePrimary} onChange={handleChange} placeholder="+1 234 567 8900" />
                {errors.mobilePrimary && <p className="mt-1 text-xs text-red-500">{errors.mobilePrimary}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Alternate Mobile Number (optional)</label>
                <Input name="mobileAlternate" type="tel" value={formData.mobileAlternate} onChange={handleChange} placeholder="Optional contact" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email ID (Primary Email) *</label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="parent@example.com" />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Create Password *</label>
                <Input name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Minimum 6 characters" />
                {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="md:col-span-2 bg-slate-50/50 p-6 rounded-2xl border border-slate-100 mb-4">
                <h3 className="text-sm font-black text-brand-700 uppercase tracking-widest mb-4">📍 Address Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Full Address *</label>
                    <textarea name="address" value={formData.address} onChange={handleChange} className="w-full p-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand-500 outline-none min-h-[80px]" placeholder="Street, House/Flat No, Area" />
                    {errors.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">City *</label>
                    <Input name="city" value={formData.city} onChange={handleChange} placeholder="City" />
                    {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Pincode *</label>
                    <Input name="pincode" value={formData.pincode} onChange={handleChange} placeholder="Pincode" />
                    {errors.pincode && <p className="mt-1 text-xs text-red-500">{errors.pincode}</p>}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
                <h3 className="text-sm font-black text-brand-700 uppercase tracking-widest mb-4">📞 Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Name *</label>
                    <Input name="emergencyName" value={formData.emergencyName} onChange={handleChange} placeholder="Full Name" />
                    {errors.emergencyName && <p className="mt-1 text-xs text-red-500">{errors.emergencyName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Relationship *</label>
                    <Input name="emergencyRelationship" value={formData.emergencyRelationship} onChange={handleChange} placeholder="e.g. Uncle" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Phone Number *</label>
                    <Input name="emergencyPhone" type="tel" value={formData.emergencyPhone} onChange={handleChange} placeholder="Phone" />
                    {errors.emergencyPhone && <p className="mt-1 text-xs text-red-500">{errors.emergencyPhone}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
               <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 mb-6">
                <h3 className="text-sm font-black text-brand-700 uppercase tracking-widest mb-4">🛡️ Insurance (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Insurance Company Name</label>
                    <Input name="insuranceCompany" value={formData.insuranceCompany} onChange={handleChange} placeholder="Insurance provider" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Policy Number</label>
                    <Input name="policyNumber" value={formData.policyNumber} onChange={handleChange} placeholder="Policy #" />
                  </div>
                </div>
              </div>

              <div className="bg-brand-50/50 p-6 rounded-2xl border border-brand-100 space-y-4">
                 <label className="flex items-start gap-3 cursor-pointer p-2 hover:bg-brand-50 rounded-lg transition-colors">
                  <input type="checkbox" name="consentGuardian" checked={formData.consentGuardian} onChange={handleChange} className="mt-1 w-4 h-4 text-brand-600 rounded border-slate-300 focus:ring-brand-500" />
                  <span className="text-sm text-slate-700 font-medium">I confirm that I am the parent/guardian and the information provided is accurate. *</span>
                </label>
                {errors.consentGuardian && <p className="text-xs text-red-500 ml-7 font-bold">{errors.consentGuardian}</p>}
                
                <label className="flex items-start gap-3 cursor-pointer p-2 hover:bg-brand-50 rounded-lg transition-colors">
                  <input type="checkbox" name="consentPolicies" checked={formData.consentPolicies} onChange={handleChange} className="mt-1 w-4 h-4 text-brand-600 rounded border-slate-300 focus:ring-brand-500" />
                  <span className="text-sm text-slate-700 font-medium">I agree to the screening evaluation and data usage policies. *</span>
                </label>
                {errors.consentPolicies && <p className="text-xs text-red-500 ml-7 font-bold">{errors.consentPolicies}</p>}
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 flex justify-between gap-4">
          <Button 
            variant="outline" 
            onClick={prevStep} 
            className={`${currentStep === 0 ? 'invisible' : 'visible'} px-6 h-12 rounded-xl`}
          >
            <ChevronLeft size={20} className="mr-2" /> Previous Step
          </Button>
          <Button 
            onClick={nextStep} 
            className="px-8 h-12 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold"
          >
            {currentStep === STEPS.length - 1 ? 'Register Patient' : 'Continue to Next Step'}
            {currentStep < STEPS.length - 1 && <ChevronRight size={20} className="ml-2" />}
          </Button>
        </div>
      </Card>
    </div>
  );
}
